import React, { useEffect, useMemo } from "react";

import { Eye, X, FileText, CheckCircle } from "lucide-react";

import Button from "../../../Components/Button";
import Table from "../../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchAssignmentsOverview,
  AssignmentsOverviewItem,
} from "../../../Store/Reducers/OpsSlice";
import AssignmentDetailsModal from "../AssignmentDetailsModal";

import "./style.scss";

// Helper function to get date threshold based on filter
const getDateThreshold = (timeFilter: string): Date | null => {
  if (timeFilter === "all") return null;

  const now = new Date();
  const threshold = new Date(now);

  switch (timeFilter) {
    case "7-days":
      threshold.setDate(now.getDate() - 7);
      break;
    case "30-days":
      threshold.setDate(now.getDate() - 30);
      break;
    case "90-days":
      threshold.setDate(now.getDate() - 90);
      break;
    default:
      return null;
  }

  return threshold;
};

interface CompletedActivationsContainerProps {
  timeFilter?: string;
}

const CompletedActivationsContainer: React.FC<
  CompletedActivationsContainerProps
> = ({ timeFilter = "all" }) => {
  const dispatch = useAppDispatch();
  const {
    data: assignmentsOverviewData,
    loading,
    error,
  } = useAppSelector(state => state.ops.assignmentsOverview);

  useEffect(() => {
    // Fetch completed activations (logged in users)
    dispatch(fetchAssignmentsOverview("logged"));
  }, [dispatch]);

  // Transform API response to flat table rows
  const tableRows = useMemo(() => {
    if (!assignmentsOverviewData?.items) return [];

    const dateThreshold = getDateThreshold(timeFilter);

    // Filter by time period first if specified
    let filteredItems = assignmentsOverviewData.items;
    if (dateThreshold) {
      filteredItems = assignmentsOverviewData.items.filter(
        (item: AssignmentsOverviewItem) => {
          if (!item.activated_date) return false;
          const activatedDate = new Date(item.activated_date);
          return activatedDate >= dateThreshold;
        }
      );
    }

    // Map filtered items to table rows
    const mappedItems = filteredItems.map((item: AssignmentsOverviewItem) => {
      // Determine batch name from batch or assignment_request
      let batchName = "No Assignment";
      const assignmentRequest = (item as any).assignment_request;
      if (item.type === "batch" && item.batch) {
        batchName = item.batch.batch_name;
      } else if (assignmentRequest?.batch_name) {
        batchName = assignmentRequest.batch_name;
      } else if (item.type === "assignment_request") {
        batchName = `Assignment: ${item.account.name}`;
      }

      return {
        id: item.id,
        executive: {
          name: item.executive.name,
          email: item.executive.email,
        },
        account: item.account.name || "No Assignment",
        batch: batchName,
        activatedDate: item.activated_date,
        loginStatus: item.login_status,
        lastLogin: item.login_status?.last_login || "Never",
        batchInfo: item.batch,
        assignmentRequestId: item.assignment_request_id,
        assignmentRequestStatus: item.assignment_request_status,
        fullItem: item,
      };
    });

    return mappedItems;
  }, [assignmentsOverviewData, timeFilter]);
  const [showDetails, setShowDetails] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState<any | null>(null);

  const openDetails = (row: any) => {
    setSelectedRow(row);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedRow(null);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 mt-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Loading completed activations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 mt-4 text-center">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <X className="w-8 h-8 text-red-600" />
        </div>
        <p className="text-base font-medium text-red-600 mb-2">
          Error loading completed activations
        </p>
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (!tableRows || tableRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 mt-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-base font-medium text-gray-600">
          No completed activations found.
        </p>
      </div>
    );
  }

  return (
    <div className="completed-activation">
      <div className="activation-table-wrapper">
        <Table
          scrollable={true}
          maxHeight="calc(100vh - 299px)"
          columns={[
            {
              key: "executive",
              title: "Executive",
              render: (_: any, record: any) => (
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {record?.executive?.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {record?.executive?.email}
                  </div>
                </div>
              ),
            },
            {
              key: "account",
              title: "Account",
              render: (_: any, record: any) => (
                <span>{record?.account || "No Assignment"}</span>
              ),
            },
            {
              key: "batch",
              title: "Batch",
              render: (_: any, record: any) => (
                <span>{record?.batch || "N/A"}</span>
              ),
            },
            {
              key: "activatedDate",
              title: "Activated Date",
              render: (_: any, record: any) => (
                <span>{record?.activatedDate || "N/A"}</span>
              ),
            },
            {
              key: "loginStatus",
              title: "Login Status",
              render: (_: any, record: any) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="w-4 h-4 mr-1.5" />
                  Logged In
                </span>
              ),
            },
            {
              key: "lastLogin",
              title: "Last Login",
              render: (_: any, record: any) => (
                <span>{record?.lastLogin || "Never"}</span>
              ),
            },
            {
              key: "actions",
              title: "Actions",
              textalign: "center",
              className: "flex justify-center",
              render: (_: any, record: any) => (
                <Button
                  variant="clear"
                  onClick={() => openDetails(record)}
                  title="View"
                  className="primary-color flex justify-center w-full"
                >
                  <Eye size={18} />
                </Button>
              ),
            },
          ]}
          data={tableRows}
          rowKey={(record: any) => record.id}
        />
      </div>

      <AssignmentDetailsModal
        isOpen={showDetails}
        onClose={closeDetails}
        selectedRow={selectedRow}
      />
    </div>
  );
};

export default CompletedActivationsContainer;
